import {Close} from '@mui/icons-material'
import {Box, ClickAwayListener, IconButton, Stack, TextField, Backdrop} from '@mui/material'
import {useState, useEffect} from 'react'
import {generateMarkerData, updateLabelsAlignments} from '../plugins/marker'
import {splitString} from '../helper'
import {useTheme} from '@mui/material/styles'
import {useChartMarker} from '../../../store/hooks/chartMarker'
import {
  CancelButton,
  MARKER_TOOLTIP_WIDTH,
  MarkerContainer,
  StyledButton,
  StyledDivider,
  StyledTypography
} from './MarkerTooltipStyle'

import PropTypes from 'prop-types'

// Helper function to copy annotation properties as plain object
const copyAnnotation = (ann, options = {}) => {
  const {isCurrentMarker = false, newContent = null} = options
  const labelContent = newContent || (Array.isArray(ann.label?.content) ? [...ann.label.content] : ann.label?.content)

  return {
    type: ann.type,
    xMin: ann.xMin,
    xMax: ann.xMax,
    xScaleID: ann.xScaleID,
    borderDash: ann.borderDash ? [...ann.borderDash] : undefined,
    label: ann.label
      ? {
          content: labelContent,
          display: ann.label.display,
          backgroundColor: ann.label.backgroundColor,
          width: ann.label.width,
          position: ann.label.position,
          padding: ann.label.padding ? {...ann.label.padding} : undefined,
          yAdjust: ann.label.yAdjust,
          z: isCurrentMarker ? 999 : ann.label.z,
          borderWidth: ann.label.borderWidth
        }
      : undefined,
    click: ann.click
  }
}

// Helper function to get all existing annotations as plain objects
const getExistingAnnotations = (chart) => {
  const existingAnnotations = {}
  const currentAnnotations = chart.marker.annotations || chart.options.plugins.annotation.annotations
  if (currentAnnotations) {
    for (const key of Object.keys(currentAnnotations)) {
      existingAnnotations[key] = copyAnnotation(currentAnnotations[key])
    }
  }
  return existingAnnotations
}

const MarkerTooltip = ({chartRef: chart, name, setClickAwayEnabled}) => {
  const theme = useTheme()
  const initDesc = chart.options.plugins.marker.enabled
    ? ''
    : chart.marker.lineLabels?.[chart?.currentEditElement?.id] || ''
  const [desc, setDesc] = useState(initDesc)
  const {updateMarker} = useChartMarker()
  const {top, height} = chart.chartArea

  const bottom = chart.canvas.parentNode.parentElement.clientHeight - (top + height) - 15 + 'px'
  const moveTooltipToLeft = chart.markerTooltipPosition.x + MARKER_TOOLTIP_WIDTH + 120 > globalThis.innerWidth
  const markerLeft = moveTooltipToLeft
    ? chart.markerTooltipPosition.x - MARKER_TOOLTIP_WIDTH - 10
    : chart.markerTooltipPosition.x + 10

  const handleClose = () => {
    chart.config.options.setShowMarkerTooltip(false)
    chart.markerTooltipPosition = null
    chart.currentEditElement = null
  }

  const handleAdd = () => {
    const id = chart.marker.lineX
    const str = splitString(desc)
    if (!str) return

    const markerData = generateMarkerData(id, str)

    // Disable marker modes FIRST before adding annotation
    chart.options.plugins.marker.enabled = false
    chart.options.plugins.marker.editEnabled = false
    chart.setIsAnyModeEnabled(false)
    setClickAwayEnabled(false)

    // Get existing annotations and add new one
    const existingAnnotations = getExistingAnnotations(chart)
    existingAnnotations[id] = markerData

    // Replace the entire annotations object
    chart.options.plugins.annotation.annotations = existingAnnotations

    // Update chart.marker.lineLabels
    const currentLineLabels = chart.marker.lineLabels || {}
    const updatedLineLabels = {...currentLineLabels, [id]: desc}
    chart.marker.lineLabels = updatedLineLabels

    // Store annotations reference on chart.marker
    chart.marker.annotations = existingAnnotations

    // Update Redux state
    updateMarker(name, updatedLineLabels)
    updateLabelsAlignments(updatedLineLabels, str.length, chart, id)

    // Update chart to render the marker
    chart.update()
    chart.markerTooltipPosition = null
    chart.currentEditElement = null
    if (chart.config.options.setShowMarkerTooltip) {
      chart.config.options.setShowMarkerTooltip(false)
    }
  }

  const handleEdit = () => {
    const {id} = chart?.currentEditElement || {}
    const str = splitString(desc)
    if (!str) return

    // Recreate all annotations as plain objects
    const existingAnnotations = {}
    const currentAnnotations = chart.marker.annotations || chart.options.plugins.annotation.annotations
    if (currentAnnotations) {
      for (const key of Object.keys(currentAnnotations)) {
        const ann = currentAnnotations[key]
        const isCurrentMarker = Number.parseInt(key) === Number.parseInt(id)
        const newContent = isCurrentMarker ? str : null
        existingAnnotations[key] = copyAnnotation(ann, {isCurrentMarker, newContent})
      }
    }

    // Update chart annotations
    chart.options.plugins.annotation.annotations = existingAnnotations
    if (chart.currentEditElement?.element?.label?.options) {
      chart.currentEditElement.element.label.options.z = 999
    }

    // Update chart.marker.lineLabels
    const currentLineLabels = chart.marker.lineLabels || {}
    const updatedLineLabels = {...currentLineLabels, [id]: desc}
    chart.marker.lineLabels = updatedLineLabels
    chart.marker.annotations = existingAnnotations

    // Update Redux state
    updateMarker(name, updatedLineLabels)
    updateLabelsAlignments(updatedLineLabels, str.length, chart, id)

    // Close tooltip before updating chart
    handleClose()
    chart.update()
  }

  const handleDelete = () => {
    const {id} = chart?.currentEditElement || {}
    if (!id && id !== 0) return

    // Create new annotations excluding the one to delete
    const newAnnotations = {}
    const currentAnnotations = chart.marker.annotations || chart.options.plugins.annotation.annotations

    for (const key of Object.keys(currentAnnotations)) {
      if (Number.parseInt(key) !== Number.parseInt(id)) {
        newAnnotations[key] = copyAnnotation(currentAnnotations[key])
      }
    }

    // Create new lineLabels excluding the one to delete
    const currentLineLabels = chart.marker.lineLabels || {}
    const newLineLabels = {}
    for (const key of Object.keys(currentLineLabels)) {
      if (Number.parseInt(key) !== Number.parseInt(id)) {
        newLineLabels[key] = currentLineLabels[key]
      }
    }

    // Update chart
    chart.options.plugins.annotation.annotations = newAnnotations
    chart.marker.lineLabels = newLineLabels
    chart.marker.annotations = newAnnotations

    // Update Redux state
    updateMarker(name, newLineLabels)
    updateLabelsAlignments(newLineLabels, 0, chart, id)

    // Disable edit mode
    chart.options.plugins.marker.editEnabled = false
    if (chart.setIsAnyModeEnabled) {
      chart.setIsAnyModeEnabled(false)
    }
    setClickAwayEnabled(false)

    // Close tooltip before updating chart
    handleClose()
    chart.update()
  }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && desc.length > 0) {
      e.preventDefault()
      if (chart.options.plugins.marker.enabled) {
        handleAdd()
      } else {
        handleEdit()
      }
    }
  }
  useEffect(() => {
    return () => {
      handleClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        position: 'absolute',
        margin: 'auto',
        top: top - 10 + 'px',
        bottom
      }}
      onClick={(e) => {
        e.stopPropagation()
        handleClose()
      }}
      open
      invisible
    >
      <ClickAwayListener onClickAway={handleClose}>
        <MarkerContainer onClick={(e) => e.stopPropagation()} style={{left: markerLeft}} elevation={3}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <StyledTypography>{`${
              chart.options.plugins.marker.enabled ? 'Add' : 'Edit'
            } Marker Description`}</StyledTypography>
            <IconButton aria-label='close' onClick={handleClose}>
              <Close htmlColor={theme.palette.hpBaseLighter} />
            </IconButton>
          </Box>
          <StyledDivider variant='fullWidth' />
          <Stack direction='column' spacing={2}>
            <Box>
              <TextField
                value={desc}
                placeholder='Enter description'
                onChange={(e) => setDesc(e.target.value)}
                helperText={`${desc.length}/50`}
                inputProps={{
                  maxLength: 50,
                  style: {padding: '8px 8px'},
                  'aria-label': 'description'
                }}
                fullWidth
                onKeyDown={handleKeyDown}
                FormHelperTextProps={{style: {textAlign: 'right', marginRight: '0px'}}}
              />
            </Box>
            <StyledButton
              actionType='add'
              onClick={(e) => {
                e.stopPropagation()
                chart.options.plugins.marker.enabled ? handleAdd() : handleEdit()
              }}
              disabled={desc.length === 0 || (!chart.options.plugins.marker.enabled && desc === initDesc)}
            >
              {chart.options.plugins.marker.enabled ? 'Add' : 'Save'}
            </StyledButton>

            {chart.options.plugins.marker.editEnabled && (
              <StyledButton
                actionType='remove'
                onClick={(e) => {
                  e.stopPropagation()
                  handleDelete()
                }}
              >
                Remove
              </StyledButton>
            )}
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
          </Stack>
        </MarkerContainer>
      </ClickAwayListener>
    </Backdrop>
  )
}

MarkerTooltip.propTypes = {
  chartRef: PropTypes.object,
  name: PropTypes.string,
  setClickAwayEnabled: PropTypes.func
}

export default MarkerTooltip

/* The MarkerTooltip component is a React functional component that provides a tooltip interface for adding or editing markers on a chart.

Function Descriptions:
  - MarkerTooltip: Main component that renders the tooltip for adding/editing markers on a chart.
  - handleClose: Closes the marker tooltip and resets its position.
  - handleAdd: Adds a new marker with the provided description to the chart.
  - handleEdit: Edits an existing marker's description and updates its alignment.
  - handleDelete: Deletes the selected marker from the chart and updates alignments.
  - handleKeyDown: Handles 'Enter' key press to add or edit a marker based on the mode.
  - useEffect cleanup: Ensures the tooltip is closed when the component unmounts.

*/
